package com.flowyride.core.map

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalLayoutDirection
import androidx.compose.ui.unit.DpOffset
import androidx.compose.ui.unit.DpSize
import com.flowyride.core.map.resources.Res
import com.flowyride.core.map.resources.ic_speed_camera
import org.jetbrains.compose.resources.painterResource
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.layers.SymbolLayer
import androidx.compose.ui.unit.dp
import com.flowyride.core.designsystem.component.SavedPlaceBadge
import com.flowyride.core.designsystem.theme.FlowyColors
import androidx.compose.foundation.clickable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.ui.draw.shadow
import com.flowyride.core.domain.model.GeoPoint
import org.maplibre.compose.camera.CameraPosition
import org.maplibre.compose.camera.CameraUpdate
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.feature
import org.maplibre.compose.expressions.dsl.switch
import com.flowyride.core.domain.analysis.SpeedBand
import com.flowyride.core.domain.analysis.SpeedStretch
import org.maplibre.compose.expressions.value.LineCap
import org.maplibre.compose.expressions.value.LineJoin
import org.maplibre.compose.interaction.ClickEvent
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.interaction.MapInteractions
import org.maplibre.compose.layers.LineLayer
import org.maplibre.compose.map.MaplibreMap
import org.maplibre.compose.map.rememberMapState
import org.maplibre.compose.map.MapState
import org.maplibre.compose.overlay.ExpandingAttributionButton
import org.maplibre.compose.overlay.MaplibreLogo
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.compose.style.BaseStyle
import org.maplibre.compose.util.DpPadding
import org.maplibre.spatialk.geojson.BoundingBox
import org.maplibre.spatialk.geojson.Position
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

/** Free OSM vector tiles, no API key. */
internal const val MAP_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty"

private val InitialCamera = CameraPosition(
    target = Position(longitude = 2.5, latitude = 46.6), // France
    zoom = 4.5,
)

private val SpeedBandColor = switch(
    feature[GeoJson.BAND_PROPERTY],
    *SpeedBand.entries.map { case(it.name, const(it.routeColor)) }.toTypedArray(),
    fallback = const(FlowyColors.RouteLine),
)

private val FitPadding = DpPadding(left = 64.dp, top = 64.dp, right = 64.dp, bottom = 64.dp)

/**
 * Displays a route track and draggable markers. The camera frames the first track of a ride, then never moves
 * by itself while the ride is edited ([CameraFraming]).
 *
 * @param obscuredInsets Map area hidden by the app's UI (panels, system bars). Only used to frame the ride
 *   in the visible part and to place the map controls: it never moves the camera by itself, so panels can
 *   expand or collapse without the map sliding underneath.
 * @param speedStretches The line colored by speed limit ([routeColor]); empty: one color.
 * @param speedCameras Drawn as warning icons (one GPU layer: fine for thousands of points).
 * @param onSpeedCameraClick A camera icon was tapped (its [MapSpeedCamera.id]).
 * @param userLocation The rider's position, drawn as a dot.
 * @param places Saved addresses, under the ride's markers; [onPlaceClick] gets the tapped one's [MapPlace.id].
 * @param centerRequest Moves the camera there (zooming in if needed), once per request.
 * @param framingKey Changing it frames the next track, as the first one of a new ride (loaded or generated).
 * @param onClick Tap or primary click on the map.
 * @param onLongClick Long press (touch) or secondary click (mouse).
 * @param onMarkerMoved A marker was dragged and dropped at a new position.
 * @param onTrackDragged The route line was grabbed at [track] index `trackIndex` and dropped at a new position.
 *   Null: the line cannot be grabbed.
 */
@Composable
fun RouteMap(
    track: List<GeoPoint>,
    markers: List<MapMarker>,
    modifier: Modifier = Modifier,
    speedStretches: List<SpeedStretch> = emptyList(),
    speedCameras: List<MapSpeedCamera> = emptyList(),
    obscuredInsets: PaddingValues = PaddingValues(0.dp),
    onSpeedCameraClick: (id: String) -> Unit = {},
    userLocation: GeoPoint? = null,
    places: List<MapPlace> = emptyList(),
    onPlaceClick: (id: Long) -> Unit = {},
    centerRequest: CenterRequest? = null,
    framingKey: Any? = null,
    onClick: (MapClick) -> Unit = {},
    onLongClick: (MapClick) -> Unit = {},
    onMarkerMoved: (markerId: String, GeoPoint) -> Unit = { _, _ -> },
    onTrackDragged: ((trackIndex: Int, GeoPoint) -> Unit)? = { _, _ -> },
) {
    val currentOnClick by rememberUpdatedState(onClick)
    val currentOnLongClick by rememberUpdatedState(onLongClick)
    val interactions = remember {
        MapInteractions {
            camera {
                // A flat north-up map is easier to read while planning.
                rotate { enabled = false }
                tilt { enabled = false }
            }
            callbacks {
                // After the layers: a tap on a speed camera goes to the camera, not to the map.
                click { onUnhandled { event -> event.dispatchTo(currentOnClick) } }
                longClick { onEvent { event -> event.dispatchTo(currentOnLongClick) } }
            }
        }
    }

    val trackGeoJson = remember(track) { track.takeIf { it.size >= 2 }?.let(GeoJson::lineString) }
    val stretchesGeoJson = remember(track, speedStretches) {
        // Stretches of another track (route being replaced) would cut outside this one.
        speedStretches.takeIf { it.isNotEmpty() && it.last().endTrackIndex == track.lastIndex }?.let { GeoJson.speedStretches(track, it) }
    }
    val speedCamerasGeoJson = remember(speedCameras) {
        speedCameras.takeIf { it.isNotEmpty() }?.let { cameras -> GeoJson.identifiedPoints(cameras.map { it.id to it.point }) }
    }
    val currentOnSpeedCameraClick by rememberUpdatedState(onSpeedCameraClick)
    val speedCameraIcon = painterResource(Res.drawable.ic_speed_camera)

    val mapState = rememberMapState(
        baseStyle = BaseStyle.Uri(MAP_STYLE_URL),
        initialCameraPosition = InitialCamera,
    ) {
        LandscapeLayers()
        if (trackGeoJson != null) {
            val trackSource = rememberGeoJsonSource(GeoJsonData.JsonString(trackGeoJson))
            LineLayer(
                id = "route-casing",
                source = trackSource,
                color = const(FlowyColors.RouteCasing),
                width = const(9.dp),
                cap = const(LineCap.Round),
                join = const(LineJoin.Round),
            )
            if (stretchesGeoJson == null) {
                LineLayer(
                    id = "route-line",
                    source = trackSource,
                    color = const(FlowyColors.RouteLine),
                    width = const(5.dp),
                    cap = const(LineCap.Round),
                    join = const(LineJoin.Round),
                )
            } else {
                LineLayer(
                    id = "route-line",
                    source = rememberGeoJsonSource(GeoJsonData.JsonString(stretchesGeoJson)),
                    color = SpeedBandColor,
                    width = const(5.dp),
                    cap = const(LineCap.Round),
                    join = const(LineJoin.Round),
                )
            }
        }
        if (speedCamerasGeoJson != null) {
            // Above the route line so a camera on the ride stays visible.
            SymbolLayer(
                id = "speed-cameras",
                source = rememberGeoJsonSource(GeoJsonData.JsonString(speedCamerasGeoJson)),
                minZoom = SPEED_CAMERAS_MIN_ZOOM,
                iconImage = image(speedCameraIcon, size = DpSize(SPEED_CAMERA_ICON_SIZE, SPEED_CAMERA_ICON_SIZE)),
                // Without it, MapLibre hides icons that collide with each other.
                iconAllowOverlap = const(SPEED_CAMERAS_SHOW_ALL),
                onClick = { features ->
                    val id = features.firstNotNullOfOrNull { it.properties?.get(GeoJson.ID_PROPERTY)?.jsonPrimitive?.contentOrNull }
                    if (id == null) {
                        ClickResult.Pass
                    } else {
                        currentOnSpeedCameraClick(id)
                        ClickResult.Consume
                    }
                },
            )
        }
    }

    val markerPoints = markers.map { it.point }
    val layoutDirection = LocalLayoutDirection.current
    val obscured = DpPadding(
        left = obscuredInsets.calculateLeftPadding(layoutDirection),
        top = obscuredInsets.calculateTopPadding(),
        right = obscuredInsets.calculateRightPadding(layoutDirection),
        bottom = obscuredInsets.calculateBottomPadding(),
    )
    // Read by the framing effect, which must not restart when a panel is resized.
    val currentObscured by rememberUpdatedState(obscured)
    val framing = remember(framingKey) { CameraFraming() }
    LaunchedEffect(mapState, track, markerPoints, framing) {
        val bounds = framing.boundsToFrame(track, markerPoints) { bounds ->
            mapState.unobscuredBounds(currentObscured)?.let(bounds::isInside) == true
        } ?: return@LaunchedEffect
        mapState.animateCameraToBounds(
            boundingBox = BoundingBox(west = bounds.west, south = bounds.south, east = bounds.east, north = bounds.north),
            fitPadding = mapState.usableObscured(currentObscured) + FitPadding,
        )
        framing.onFramed(track)
    }

    LaunchedEffect(mapState, centerRequest) {
        val request = centerRequest ?: return@LaunchedEffect
        mapState.animateCamera(
            CameraUpdate(
                target = request.point.toPosition(),
                zoom = maxOf(mapState.cameraPosition.zoom, CENTER_MIN_ZOOM),
                padding = mapState.usableObscured(currentObscured),
            ),
        )
    }

    MaplibreMap(
        modifier = modifier,
        state = mapState,
        interactions = interactions,
    ) {
        // Default controls, lifted above the app's panels (MapOverlay.Default would follow viewport insets).
        Box(Modifier.matchParentSize().padding(obscuredInsets).padding(8.dp)) {
            MaplibreLogo(Modifier.align(Alignment.BottomStart))
            ExpandingAttributionButton(Modifier.align(Alignment.BottomEnd), contentAlignment = Alignment.BottomEnd)
        }
        // Below the markers: grabbing a marker takes precedence over grabbing the line.
        onTrackDragged?.let { TrackDragLayer(track, markerPoints, mapState, it) }
        places.forEach { place ->
            key("place-${place.id}") {
                SavedPlaceBadge(
                    emoji = place.emoji,
                    color = place.color,
                    size = if (place.emoji == null) PlaceDotSize else PlaceBadgeSize,
                    modifier = Modifier
                        .placedAt(place.point.toPosition())
                        .shadow(3.dp, CircleShape)
                        .clickable { onPlaceClick(place.id) },
                )
            }
        }
        userLocation?.let { location ->
            Box(Modifier.placedAt(location.toPosition()).size(UserDotSize)) {
                Canvas(Modifier.matchParentSize()) {
                    drawCircle(FlowyColors.Stop.copy(alpha = 0.2f))
                    drawCircle(Color.White, radius = size.minDimension * 0.3f)
                    drawCircle(FlowyColors.Stop, radius = size.minDimension * 0.22f)
                }
            }
        }
        markers.forEach { marker ->
            key(marker.id) {
                DraggableMarker(marker, mapState) { point -> onMarkerMoved(marker.id, point) }
            }
        }
    }
}


/** Centring on the rider shows at least the streets around. */
private const val CENTER_MIN_ZOOM = 13.0
private val UserDotSize = 28.dp
private val PlaceBadgeSize = 30.dp
private val PlaceDotSize = 16.dp
/**
 * TEMPORARY: every camera at every zoom, to eyeball the dataset.
 * Back to normal: false (the zoom is then 7, and colliding icons are thinned out).
 */
private const val SPEED_CAMERAS_SHOW_ALL = false

/** Below that zoom, thousands of icons are just noise. */
private val SPEED_CAMERAS_MIN_ZOOM = if (SPEED_CAMERAS_SHOW_ALL) 0f else 7f
private val SPEED_CAMERA_ICON_SIZE = 22.dp

private operator fun DpPadding.plus(other: DpPadding) =
    DpPadding(left + other.left, top + other.top, right + other.right, bottom + other.bottom)

/** Smallest map area worth framing into; below it (e.g. keyboard open), panels are ignored. */
private val MinFramingArea = 160.dp

/**
 * [obscured], or no inset on an axis where panels leave too little room to frame anything:
 * MapLibre would otherwise not move at all.
 */
private fun MapState.usableObscured(obscured: DpPadding): DpPadding {
    val size = viewport?.size ?: return obscured
    val horizontalRoom = size.width - obscured.left - obscured.right - FitPadding.left - FitPadding.right
    val verticalRoom = size.height - obscured.top - obscured.bottom - FitPadding.top - FitPadding.bottom
    return DpPadding(
        left = if (horizontalRoom < MinFramingArea) 0.dp else obscured.left,
        right = if (horizontalRoom < MinFramingArea) 0.dp else obscured.right,
        top = if (verticalRoom < MinFramingArea) 0.dp else obscured.top,
        bottom = if (verticalRoom < MinFramingArea) 0.dp else obscured.bottom,
    )
}

/** Geographic bounds of the part of the map not hidden by [obscured] (north-up map). */
private fun MapState.unobscuredBounds(obscured: DpPadding): Bounds? {
    val size = viewport?.size ?: return null
    val topLeft = positionFromScreenLocation(DpOffset(obscured.left, obscured.top)) ?: return null
    val bottomRight = positionFromScreenLocation(DpOffset(size.width - obscured.right, size.height - obscured.bottom)) ?: return null
    return Bounds(west = topLeft.longitude, south = bottomRight.latitude, east = bottomRight.longitude, north = topLeft.latitude)
}

private fun ClickEvent.dispatchTo(handler: (MapClick) -> Unit): ClickResult {
    val position = position ?: return ClickResult.Pass
    val point = GeoPoint(latitude = position.latitude, longitude = normalizeLongitude(position.longitude))
    handler(MapClick(point, screenOffset))
    return ClickResult.Consume
}
